import React, { useState, useEffect, useRef, useCallback } from 'react'
import { HashLink } from 'react-router-hash-link'
import { Container } from './styles'
import { Magnetic } from '../Magnetic/Magnetic'

function useScrollSpy(ids: string[], offset = 80) {
  const [activeId, setActiveId] = useState('')
  const pendingId = useRef<string | null>(null)
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleScroll = useCallback(() => {
    // While a nav-click scroll is in flight, keep the pending section active
    // and only reset once the page has stopped scrolling (150 ms debounce).
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current)

    if (pendingId.current !== null) {
      setActiveId(pendingId.current)
      scrollEndTimer.current = setTimeout(() => {
        pendingId.current = null
      }, 150)
      return
    }

    let current = ''
    for (const id of ids) {
      const el = document.getElementById(id)
      if (el && el.getBoundingClientRect().top <= offset + 10) {
        current = id
      }
    }
    setActiveId(current)
  }, [ids, offset])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current)
    }
  }, [handleScroll])

  // Expose a setter so nav links can register the intended destination
  const setPending = useCallback((id: string) => {
    pendingId.current = id
    setActiveId(id)
  }, [])

  return { activeId, setPending }
}

/**
 * Smoothly scrolls to a section by id.
 * Handles LazySection placeholders: if the section hasn't rendered its
 * children yet (minHeight placeholder), it briefly scrolls the element
 * near the viewport to trigger the IntersectionObserver, waits for the
 * real content to mount, then performs the final smooth scroll.
 */
function scrollToSection(id: string) {
  const el = document.getElementById(id)
  if (!el) return

  const HEADER_OFFSET = 80 // px — matches the fixed header height

  function doScroll() {
    const top = el!.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
    window.scrollTo({ top, behavior: 'smooth' })
  }

  // Check if the LazySection placeholder is still active.
  // A placeholder has a fixed minHeight style set inline and no rendered child sections.
  const hasRenderedContent = el.children.length > 0 && (el.children[0] as HTMLElement).tagName === 'SECTION'

  if (hasRenderedContent) {
    // Content already rendered — scroll immediately
    doScroll()
    return
  }

  // Content not yet rendered: scroll element near viewport to trigger IntersectionObserver
  el.scrollIntoView({ block: 'nearest' })

  // Poll until the real content appears (max ~800 ms)
  let attempts = 0
  const interval = setInterval(() => {
    attempts++
    const loaded = el.children.length > 0 && (el.children[0] as HTMLElement).tagName === 'SECTION'
    if (loaded || attempts > 16) {
      clearInterval(interval)
      // Small rAF to let the browser paint the new layout
      requestAnimationFrame(() => requestAnimationFrame(doScroll))
    }
  }, 50)
}

export function Header() {
  const [isActive, setActive] = useState(false)
  const [isLight, setIsLight] = useState(false)
  const sections = ['home', 'about', 'project', 'testimonials', 'contact']
  const { activeId, setPending } = useScrollSpy(sections)

  useEffect(() => {
    let html = document.getElementsByTagName('html')[0]
    setIsLight(html.classList.contains('light'))
  }, [])

  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isActive])

  function toggleTheme() {
    let html = document.getElementsByTagName('html')[0]
    const nextLight = html.classList.toggle('light')
    setIsLight(nextLight)
  }
  function closeMenu() {
    setActive(false)
  }

  function handleNavClick(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault()
    closeMenu()
    setPending(id)
    scrollToSection(id)
  }

  return (
    <Container className="header-fixed">
        <HashLink smooth to="#home" className="logo" aria-label="Kedar Soni — Home">
          <span className="logo-text">Kedar<span className="logo-dot">.</span></span>
        </HashLink>
        <nav className={isActive ? 'active' : ''} id="main-nav" role="navigation" aria-label="Primary navigation">
          <Magnetic strength={0.2}>
            <a href="#about" onClick={(e) => handleNavClick(e, 'about')} className={activeId === 'about' ? 'nav-active' : ''}>
              About me
            </a>
          </Magnetic>
          <Magnetic strength={0.2}>
            <a href="#project" onClick={(e) => handleNavClick(e, 'project')} className={activeId === 'project' ? 'nav-active' : ''}>
              Projects
            </a>
          </Magnetic>
          <Magnetic strength={0.2}>
            <a href="#testimonials" onClick={(e) => handleNavClick(e, 'testimonials')} className={activeId === 'testimonials' ? 'nav-active' : ''}>
              Testimonials
            </a>
          </Magnetic>
          <Magnetic strength={0.2}>
            <a href="#contact" onClick={(e) => handleNavClick(e, 'contact')} className={activeId === 'contact' ? 'nav-active' : ''}>
              Contact
            </a>
          </Magnetic>
          <Magnetic>
            <a href={`${process.env.PUBLIC_URL}/KedarSoniResume.pdf`} target="_blank" rel="noreferrer" className="button">
              Resume
            </a>
          </Magnetic>
        </nav>
        <button
          type="button"
          className="theme-btn"
          onClick={toggleTheme}
          aria-label={isLight ? "Switch to Dark mode" : "Switch to Light mode"}
          title={isLight ? "Switch to Dark mode" : "Switch to Light mode"}
        >
          {isLight ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/>
              <line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          )}
        </button>
        <button
          type="button"
          aria-expanded={isActive ? 'true' : 'false'}
          aria-haspopup="true"
          aria-controls="main-nav"
          aria-label={isActive ? 'Close menu' : 'Open menu'}
          className={isActive ? 'menu active' : 'menu'}
          onClick={() => {
            setActive(!isActive)
          }}
        ></button>
    </Container>
  )
}
